import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Icon, SelectMenu } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import useStyles from './styles';
import { maxPositiveTierUnitQuantity } from '../tierLabels';
export const PRICING_TYPE_FIXED = 'Fixed price';
export const PRICING_TYPE_TIERED = 'Tiered price';
const defaultTiers = () => [{
  id: 1,
  unitsQuantity: '',
  pricePerUnit: ''
}];
function isFormTierOpenEnded(t) {
  var _t$unitsQuantity;
  const u = ((_t$unitsQuantity = t.unitsQuantity) !== null && _t$unitsQuantity !== void 0 ? _t$unitsQuantity : '').trim();
  if (u === '') return true;
  const n = parseInt(u, 10);
  return !Number.isNaN(n) && n === 0;
}

/** Modal tiers: open-ended (empty or 0 units) always last; stable order otherwise. */
function sortFormTiersOpenEndedLast(tiers) {
  if (!(tiers !== null && tiers !== void 0 && tiers.length)) return tiers;
  return [...tiers].map((t, i) => ({
    t,
    i
  })).sort((a, b) => {
    const ao = isFormTierOpenEnded(a.t) ? 1 : 0;
    const bo = isFormTierOpenEnded(b.t) ? 1 : 0;
    if (ao !== bo) return ao - bo;
    return a.i - b.i;
  }).map(x => x.t);
}

/** Allow only digits (non-negative integers) */
function sanitizeIntegerInput(value) {
  return String(value !== null && value !== void 0 ? value : '').replace(/\D/g, '');
}

/** Allow only digits and at most one decimal point with up to 2 digits after it */
function sanitizePriceInput(value) {
  let s = String(value !== null && value !== void 0 ? value : '').replace(/[^\d.]/g, '');
  const dotIndex = s.indexOf('.');
  if (dotIndex !== -1) {
    s = s.slice(0, dotIndex + 1) + s.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);
  }
  return s;
}
export const AddPricingModalContent = observer(/*#__PURE__*/forwardRef(function AddPricingModalContent(_ref, ref) {
  var _store$stripe$product, _store$stripe$meters, _meterOptionNames$;
  let isOpen = _ref.isOpen,
    isEdit = _ref.isEdit,
    pricing = _ref.pricing,
    defaultProductId = _ref.defaultProductId,
    submitError = _ref.submitError;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    pricingName = _useState2[0],
    setPricingName = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    productId = _useState4[0],
    setProductId = _useState4[1];
  const _useState5 = useState(PRICING_TYPE_FIXED),
    _useState6 = _slicedToArray(_useState5, 2),
    pricingType = _useState6[0],
    setPricingType = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    fixedPrice = _useState8[0],
    setFixedPrice = _useState8[1];
  const _useState9 = useState(defaultTiers),
    _useState0 = _slicedToArray(_useState9, 2),
    tiers = _useState0[0],
    setTiers = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    minimumPrice = _useState10[0],
    setMinimumPrice = _useState10[1];
  const _useState11 = useState([]),
    _useState12 = _slicedToArray(_useState11, 2),
    subscriptionIds = _useState12[0],
    setSubscriptionIds = _useState12[1];
  const _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    meterEvent = _useState14[0],
    setMeterEvent = _useState14[1];
  const productOptions = (_store$stripe$product = store.stripe.productList) !== null && _store$stripe$product !== void 0 ? _store$stripe$product : [];
  const meterOptions = (_store$stripe$meters = store.stripe.meters) !== null && _store$stripe$meters !== void 0 ? _store$stripe$meters : [];
  const meterOptionNames = meterOptions.map(m => {
    var _m$name;
    return ((_m$name = m.name) !== null && _m$name !== void 0 ? _m$name : '').trim();
  }).filter(Boolean);
  const firstMeterOptionName = (_meterOptionNames$ = meterOptionNames[0]) !== null && _meterOptionNames$ !== void 0 ? _meterOptionNames$ : '';
  const meterNamesKey = meterOptionNames.join('|');
  /** Keeps Select value valid before/without effect sync; prefers saved choice when still in list. */
  const resolvedMeterEvent = meterOptionNames.length && meterEvent && meterOptionNames.includes(meterEvent) ? meterEvent : firstMeterOptionName;
  const orderedTiers = sortFormTiersOpenEndedLast(tiers);
  useEffect(() => {
    var _store$stripe$meters2, _names$;
    if (!isOpen) return;
    const names = ((_store$stripe$meters2 = store.stripe.meters) !== null && _store$stripe$meters2 !== void 0 ? _store$stripe$meters2 : []).map(m => {
      var _m$name2;
      return ((_m$name2 = m.name) !== null && _m$name2 !== void 0 ? _m$name2 : '').trim();
    }).filter(Boolean);
    const first = (_names$ = names[0]) !== null && _names$ !== void 0 ? _names$ : '';
    if (isEdit && pricing) {
      var _pricing$prices$find$, _pricing$prices, _pricing$prices2, _pricing$minimumPrice, _pricing$tierTableRes2, _pricing$subscription;
      setPricingName(pricing.name || '');
      setProductId(pricing.productId || '');
      setPricingType(pricing.type === 'Tiered' ? PRICING_TYPE_TIERED : PRICING_TYPE_FIXED);
      const fixedAmount = (_pricing$prices$find$ = (_pricing$prices = pricing.prices) === null || _pricing$prices === void 0 || (_pricing$prices = _pricing$prices.find(p => p.type === 'Fixed')) === null || _pricing$prices === void 0 ? void 0 : _pricing$prices.amount) !== null && _pricing$prices$find$ !== void 0 ? _pricing$prices$find$ : pricing.fixedPrice;
      setFixedPrice(fixedAmount != null ? String(fixedAmount).replace(/^\$?\s*/, '') : '0.00');
      if (((_pricing$prices2 = pricing.prices) === null || _pricing$prices2 === void 0 ? void 0 : _pricing$prices2.length) > 1 || pricing.type === 'Tiered') {
        var _loaded, _pricing$tierTableRes;
        let loaded = pricing.tiers;
        if (!((_loaded = loaded) !== null && _loaded !== void 0 && _loaded.length) && (_pricing$tierTableRes = pricing.tierTableResponse) !== null && _pricing$tierTableRes !== void 0 && (_pricing$tierTableRes = _pricing$tierTableRes.tierPrice) !== null && _pricing$tierTableRes !== void 0 && _pricing$tierTableRes.length) {
          loaded = pricing.tierTableResponse.tierPrice.map((tier, i) => {
            var _tier$unitQuantity;
            return {
              id: i + 1,
              unitsQuantity: tier.unitQuantity === 0 || tier.unitQuantity === '0' ? '' : String((_tier$unitQuantity = tier.unitQuantity) !== null && _tier$unitQuantity !== void 0 ? _tier$unitQuantity : ''),
              pricePerUnit: tier.amount != null ? String(tier.amount).replace(/^\$?\s*/, '') : '0.00'
            };
          });
        }
        setTiers(sortFormTiersOpenEndedLast((loaded || defaultTiers()).map((t, i) => {
          var _t$id, _t$unitsQuantity2, _t$pricePerUnit;
          return {
            id: (_t$id = t.id) !== null && _t$id !== void 0 ? _t$id : i + 1,
            unitsQuantity: String((_t$unitsQuantity2 = t.unitsQuantity) !== null && _t$unitsQuantity2 !== void 0 ? _t$unitsQuantity2 : ''),
            pricePerUnit: String((_t$pricePerUnit = t.pricePerUnit) !== null && _t$pricePerUnit !== void 0 ? _t$pricePerUnit : '0.00').replace(/^\$?\s*/, '')
          };
        })));
      } else {
        setTiers(defaultTiers());
      }
      const minPrice = (_pricing$minimumPrice = pricing.minimumPrice) !== null && _pricing$minimumPrice !== void 0 ? _pricing$minimumPrice : (_pricing$tierTableRes2 = pricing.tierTableResponse) === null || _pricing$tierTableRes2 === void 0 ? void 0 : _pricing$tierTableRes2.minimumPrice;
      setMinimumPrice(minPrice != null ? String(minPrice).replace(/^\$?\s*/, '') : '0.00');
      setSubscriptionIds((_pricing$subscription = pricing.subscriptionIds) !== null && _pricing$subscription !== void 0 ? _pricing$subscription : []);
      const savedMeter = pricing.meterEvent != null && String(pricing.meterEvent).trim() !== '' ? String(pricing.meterEvent).trim() : '';
      setMeterEvent(savedMeter && names.includes(savedMeter) ? savedMeter : first);
    } else {
      setPricingName('');
      setProductId(defaultProductId || '');
      setPricingType(PRICING_TYPE_FIXED);
      setFixedPrice('');
      setTiers(defaultTiers());
      setMinimumPrice('');
      setSubscriptionIds([]);
      setMeterEvent(first);
    }
  }, [isOpen, isEdit, pricing, defaultProductId, meterNamesKey, store.stripe]);
  const handlePricingTypeChange = (_, value) => {
    if (value) setPricingType(value);
  };
  const handleTierChange = (id, field, value) => {
    setTiers(prev => prev.map(t => t.id === id ? _objectSpread(_objectSpread({}, t), {}, {
      [field]: value
    }) : t));
  };
  const handleAddTier = () => {
    const nextId = Math.max(...tiers.map(t => t.id), 0) + 1;
    setTiers(prev => [...prev, {
      id: nextId,
      unitsQuantity: '',
      pricePerUnit: ''
    }]);
  };
  const handleRemoveTier = id => {
    if (tiers.length <= 1) return;
    setTiers(prev => prev.filter(t => t.id !== id));
  };

  /** Placeholder for Units quantity; last row matches open-ended tier: "> max(prev caps)" (see tierLabels). */
  const getUnitsQuantityPlaceholder = index => {
    var _rows$unitsQuantity, _rows;
    const rows = orderedTiers;
    const isFirst = index === 0;
    const isLast = index === rows.length - 1;
    if (isFirst) return '> 0';
    if (isLast) {
      const prevRows = rows.slice(0, -1).map(t => {
        var _t$unitsQuantity3;
        const u = ((_t$unitsQuantity3 = t.unitsQuantity) !== null && _t$unitsQuantity3 !== void 0 ? _t$unitsQuantity3 : '').trim();
        const n = parseInt(u, 10);
        return {
          unitQuantity: u !== '' && !Number.isNaN(n) ? n : 0
        };
      });
      const cap = maxPositiveTierUnitQuantity(prevRows);
      return cap > 0 ? "> ".concat(cap) : '> 0';
    }
    let running = 0;
    for (let i = 0; i < index; i++) {
      var _rows$i$unitsQuantity, _rows$i;
      const filled = ((_rows$i$unitsQuantity = (_rows$i = rows[i]) === null || _rows$i === void 0 ? void 0 : _rows$i.unitsQuantity) !== null && _rows$i$unitsQuantity !== void 0 ? _rows$i$unitsQuantity : '').trim();
      if (filled) {
        const num = parseInt(filled, 10);
        running = Number.isNaN(num) ? running + 1 : num;
      } else {
        running += 1;
      }
    }
    const prevFilled = ((_rows$unitsQuantity = (_rows = rows[index - 1]) === null || _rows === void 0 ? void 0 : _rows.unitsQuantity) !== null && _rows$unitsQuantity !== void 0 ? _rows$unitsQuantity : '').trim();
    const implied = prevFilled ? Number.isNaN(parseInt(prevFilled, 10)) ? running + 1 : parseInt(prevFilled, 10) : running + 1;
    return prevFilled ? "> ".concat(implied) : String(implied);
  };
  const validate = () => !!pricingName.trim() && (pricingType === PRICING_TYPE_FIXED || orderedTiers.length >= 1 && orderedTiers.every((t, i) => {
    var _t$unitsQuantity4, _t$pricePerUnit2;
    const u = ((_t$unitsQuantity4 = t.unitsQuantity) !== null && _t$unitsQuantity4 !== void 0 ? _t$unitsQuantity4 : '').trim();
    const price = ((_t$pricePerUnit2 = t.pricePerUnit) !== null && _t$pricePerUnit2 !== void 0 ? _t$pricePerUnit2 : '').trim();
    const isLast = i === orderedTiers.length - 1;
    if (isLast) {
      return u !== '' || price !== '';
    }
    return u !== '';
  }));
  const getPayload = () => {
    var _sortedTiers$map;
    const sortedTiers = pricingType === PRICING_TYPE_TIERED ? sortFormTiersOpenEndedLast(tiers) : null;
    return {
      name: pricingName,
      productId,
      type: pricingType === PRICING_TYPE_TIERED ? 'Tiered' : 'Fixed',
      fixedPrice: pricingType === PRICING_TYPE_FIXED ? "$".concat(fixedPrice) : null,
      tiers: (_sortedTiers$map = sortedTiers === null || sortedTiers === void 0 ? void 0 : sortedTiers.map((t, i) => {
        var _t$unitsQuantity5, _t$pricePerUnit3;
        const isLast = i === sortedTiers.length - 1;
        const u = ((_t$unitsQuantity5 = t.unitsQuantity) !== null && _t$unitsQuantity5 !== void 0 ? _t$unitsQuantity5 : '').trim();
        const hasPrice = ((_t$pricePerUnit3 = t.pricePerUnit) !== null && _t$pricePerUnit3 !== void 0 ? _t$pricePerUnit3 : '').trim() !== '';
        const unitsQuantity = isLast && u === '' && hasPrice ? '0' : t.unitsQuantity;
        return {
          unitsQuantity,
          pricePerUnit: "$".concat(t.pricePerUnit)
        };
      })) !== null && _sortedTiers$map !== void 0 ? _sortedTiers$map : null,
      minimumPrice: "$".concat(minimumPrice),
      subscriptionIds,
      meterEvent: resolvedMeterEvent !== null && resolvedMeterEvent !== void 0 && resolvedMeterEvent.trim() ? resolvedMeterEvent : undefined
    };
  };
  useImperativeHandle(ref, () => ({
    validate,
    getPayload
  }));
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.content
  }, submitError && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "error",
    sx: {
      mb: 2
    }
  }, submitError), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Pricing name"), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    variant: "outlined",
    value: pricingName,
    onChange: e => setPricingName(e.target.value),
    placeholder: "Add pricing name"
  })), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Product"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "add-pricing-product",
    labelId: "add-pricing-product-label",
    label: "Select product",
    value: productId,
    onChange: e => setProductId(e.target.value),
    fullWidth: true,
    displayEmpty: true,
    disabled: true,
    renderValue: v => {
      var _productOptions$find$, _productOptions$find;
      return (_productOptions$find$ = (_productOptions$find = productOptions.find(p => p.id === v)) === null || _productOptions$find === void 0 ? void 0 : _productOptions$find.name) !== null && _productOptions$find$ !== void 0 ? _productOptions$find$ : 'Select product';
    }
  }, productOptions.map(p => /*#__PURE__*/React.createElement(MenuItem, {
    key: p.id,
    value: p.id
  }, p.name)))), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Metered Units"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "add-pricing-meter-event",
    labelId: "add-pricing-meter-event-label",
    label: "Selected Metered Units",
    value: resolvedMeterEvent,
    onChange: e => setMeterEvent(e.target.value),
    fullWidth: true,
    displayEmpty: true,
    disabled: meterOptionNames.length === 0,
    renderValue: v => v || '—'
  }, meterOptionNames.map(name => /*#__PURE__*/React.createElement(MenuItem, {
    key: name,
    value: name
  }, name)))), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Pricing type"), /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    value: pricingType,
    exclusive: true,
    onChange: handlePricingTypeChange,
    className: classes.pricingTypeToggle
  }, /*#__PURE__*/React.createElement(ToggleButton, {
    value: PRICING_TYPE_FIXED
  }, "Fixed price"), /*#__PURE__*/React.createElement(ToggleButton, {
    value: PRICING_TYPE_TIERED
  }, "Tiered price"))), pricingType === PRICING_TYPE_FIXED && /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Fixed price"), /*#__PURE__*/React.createElement(TextField, {
    variant: "outlined",
    value: fixedPrice,
    onChange: e => setFixedPrice(sanitizePriceInput(e.target.value)),
    InputProps: {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, "$")
    },
    placeholder: "0.00",
    inputProps: {
      inputMode: 'decimal'
    }
  })), pricingType === PRICING_TYPE_TIERED && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Tiered pricing"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.sectionDescription
  }, "Define quantity tiers and pricing"), /*#__PURE__*/React.createElement(Table, {
    size: "small",
    className: classes.tiersTable
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Tier"), /*#__PURE__*/React.createElement(TableCell, null, "Units quantity"), /*#__PURE__*/React.createElement(TableCell, null, "Price per unit"), /*#__PURE__*/React.createElement(TableCell, {
    width: 48
  }))), /*#__PURE__*/React.createElement(TableBody, null, orderedTiers.map((tier, index) => /*#__PURE__*/React.createElement(TableRow, {
    key: tier.id
  }, /*#__PURE__*/React.createElement(TableCell, null, index + 1), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    value: tier.unitsQuantity,
    onChange: e => handleTierChange(tier.id, 'unitsQuantity', sanitizeIntegerInput(e.target.value)),
    placeholder: getUnitsQuantityPlaceholder(index),
    className: classes.tierUnitsInput,
    inputProps: {
      inputMode: 'numeric'
    }
  })), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    value: tier.pricePerUnit,
    onChange: e => handleTierChange(tier.id, 'pricePerUnit', sanitizePriceInput(e.target.value)),
    InputProps: {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, "$")
    },
    className: classes.tierPriceInput,
    placeholder: "0.00",
    inputProps: {
      inputMode: 'decimal'
    }
  })), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    onClick: () => handleRemoveTier(tier.id),
    disabled: orderedTiers.length <= 1,
    "aria-label": "Delete tier"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))))))), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: handleAddTier,
    className: classes.addTierButton
  }, "Add tier"))), /*#__PURE__*/React.createElement(Box, {
    className: classes.section
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle1",
    className: classes.sectionTitle
  }, "Minimum price per property"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.sectionDescription
  }, "Minimum total charge per billing period for this pricing (fixed or tiered)."), /*#__PURE__*/React.createElement(TextField, {
    variant: "outlined",
    value: minimumPrice,
    onChange: e => setMinimumPrice(sanitizePriceInput(e.target.value)),
    InputProps: {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, "$")
    },
    className: classes.currencyInput,
    placeholder: "0.00",
    inputProps: {
      inputMode: 'decimal'
    }
  })));
}));